import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import { Plus, Trash2, ChevronDown, ChevronRight, ShieldCheck, Mail } from "lucide-react";

/**
 * Full email-template editor. Lists every template of the current workspace,
 * lets the user rename + edit subject + body inline. The `hu_reminder`
 * system template cannot be deleted (backend enforces this too).
 */
export default function EmailTemplateEditor() {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [templates, setTemplates] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [drafts, setDrafts] = useState({}); // id -> {name, subject, body}
  const [newOpen, setNewOpen] = useState(false);
  const [newForm, setNewForm] = useState({ name: "", subject: "", body: "" });

  const load = async () => {
    try {
      const { data } = await api.get("/email-templates");
      setTemplates(data || []);
    } catch (_) { setTemplates([]); }
  };
  useEffect(() => { load(); }, []);

  const draft = (id) => drafts[id] || {
    name: templates.find((t) => t.id === id)?.name || "",
    subject: templates.find((t) => t.id === id)?.subject || "",
    body: templates.find((t) => t.id === id)?.body || "",
  };

  const setDraftField = (id, k, v) =>
    setDrafts((d) => ({ ...d, [id]: { ...draft(id), [k]: v } }));

  const save = async (id) => {
    const patch = drafts[id];
    if (!patch) return;
    try {
      await api.patch(`/email-templates/${id}`, patch);
      toast.success(t("emails.template_saved"));
      setDrafts((d) => { const n = { ...d }; delete n[id]; return n; });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const del = async (t0) => {
    if (t0.kind === "hu_reminder") return;
    if (!(await confirm())) return;
    try {
      await api.delete(`/email-templates/${t0.id}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const create = async () => {
    if (!newForm.name.trim() || !newForm.subject.trim()) {
      toast.error(t("emails.template_name"));
      return;
    }
    try {
      await api.post("/email-templates", {
        name: newForm.name.trim(),
        subject: newForm.subject.trim(),
        body: newForm.body,
        kind: "custom",
      });
      toast.success(t("emails.template_saved"));
      setNewOpen(false);
      setNewForm({ name: "", subject: "", body: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  return (
    <div className="surface rounded-xl p-6 space-y-4" data-testid="template-editor">
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-widest text-zinc-500 flex items-center gap-2">
          <Mail className="h-3.5 w-3.5" /> {t("emails.templates_title")}
        </div>
        <Button
          size="sm"
          onClick={() => setNewOpen((v) => !v)}
          className="bg-blue-500 hover:bg-blue-600"
          data-testid="template-new-btn"
        >
          <Plus className="h-4 w-4 mr-1" /> {t("emails.template_new")}
        </Button>
      </div>
      <div className="text-[11px] text-zinc-500">{t("emails.templates_hint")}</div>

      {newOpen && (
        <div className="rounded-lg border border-blue-500/40 bg-blue-500/5 p-4 space-y-3">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">{t("emails.template_name")}</Label>
              <Input
                value={newForm.name}
                onChange={(e) => setNewForm({ ...newForm, name: e.target.value })}
                className="bg-[#171B24] border-[#2E3241]"
                data-testid="new-template-name"
              />
            </div>
            <div>
              <Label className="text-xs">{t("emails.subject")}</Label>
              <Input
                value={newForm.subject}
                onChange={(e) => setNewForm({ ...newForm, subject: e.target.value })}
                className="bg-[#171B24] border-[#2E3241]"
                data-testid="new-template-subject"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">{t("emails.body")}</Label>
            <Textarea
              rows={5}
              value={newForm.body}
              onChange={(e) => setNewForm({ ...newForm, body: e.target.value })}
              className="bg-[#171B24] border-[#2E3241] font-mono text-sm"
              data-testid="new-template-body"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setNewOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              onClick={create}
              className="bg-blue-500 hover:bg-blue-600"
              data-testid="new-template-save-btn"
            >
              {t("common.create")}
            </Button>
          </div>
        </div>
      )}

      <ul className="space-y-2">
        {templates.map((tpl) => {
          const isOpen = openId === tpl.id;
          const d = draft(tpl.id);
          const dirty = !!drafts[tpl.id];
          const isSystem = tpl.kind === "hu_reminder";
          return (
            <li
              key={tpl.id}
              className="rounded-lg border border-[#2E3241] bg-[#171B24] overflow-hidden"
              data-testid={`template-row-${tpl.kind}`}
            >
              <button
                onClick={() => setOpenId(isOpen ? null : tpl.id)}
                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-[#2A2F3A] text-left"
                data-testid={`template-toggle-${tpl.kind}`}
              >
                {isOpen ? <ChevronDown className="h-4 w-4 text-zinc-400" /> : <ChevronRight className="h-4 w-4 text-zinc-400" />}
                <span className="font-medium text-zinc-100 flex-1">{tpl.name}</span>
                {isSystem && (
                  <span className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-amber-400">
                    <ShieldCheck className="h-3 w-3" /> {t("emails.system_template")}
                  </span>
                )}
                {dirty && (
                  <span className="text-[10px] uppercase tracking-wider text-blue-400">•</span>
                )}
              </button>
              {isOpen && (
                <div className="p-4 border-t border-[#2E3241] space-y-3">
                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">{t("emails.template_name")}</Label>
                      <Input
                        value={d.name}
                        onChange={(e) => setDraftField(tpl.id, "name", e.target.value)}
                        disabled={isSystem}
                        className="bg-[#21252E] border-[#2E3241]"
                        data-testid={`template-name-${tpl.kind}`}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">{t("emails.subject")}</Label>
                      <Input
                        value={d.subject}
                        onChange={(e) => setDraftField(tpl.id, "subject", e.target.value)}
                        className="bg-[#21252E] border-[#2E3241]"
                        data-testid={`template-subject-${tpl.kind}`}
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">{t("emails.body")}</Label>
                    <Textarea
                      rows={8}
                      value={d.body}
                      onChange={(e) => setDraftField(tpl.id, "body", e.target.value)}
                      className="bg-[#21252E] border-[#2E3241] font-mono text-sm"
                      data-testid={`template-body-${tpl.kind}`}
                    />
                    <div className="text-[11px] text-zinc-500 mt-1">
                      {t("emails.placeholders_hint")}
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-1">
                    {!isSystem ? (
                      <Button
                        variant="ghost"
                        onClick={() => del(tpl)}
                        className="text-zinc-400 hover:text-red-400"
                        data-testid={`template-delete-${tpl.kind}`}
                      >
                        <Trash2 className="h-4 w-4 mr-1" /> {t("common.delete")}
                      </Button>
                    ) : <div />}
                    <Button
                      onClick={() => save(tpl.id)}
                      disabled={!dirty}
                      className="bg-blue-500 hover:bg-blue-600 disabled:opacity-50"
                      data-testid={`template-save-${tpl.kind}`}
                    >
                      {t("common.save")}
                    </Button>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
